import {
  CountryCode,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/examples.mobile.json';

export interface Country {
  iso2: CountryCode;
  dialCode: string;
  flag: string;
  name: string;
}

// ISO2 2글자를 국기 이모지(regional indicator symbol)로 변환. 'KR' -> 🇰🇷
// 0x1F1E6('A') - 'A'.charCodeAt(0)(65) = 0x1F1A5 오프셋.
function toFlagEmoji(iso2: string): string {
  return iso2.toUpperCase().replace(/[A-Z]/g, (char) => String.fromCodePoint(0x1f1a5 + char.charCodeAt(0)));
}

// 드롭다운 상단에 이 순서대로 먼저 노출할 자주 쓰는 국가. 나머지는 한글 국가명 가나다순.
// 주의: dialCode '1'은 미국/캐나다 공통(NANP)이라 다이얼 코드만으로는 구분할 수 없다. 이 순서
// 덕분에 matchCountryByDigitPrefix 가 '+1'에서 미국을 고른다 — 정확한 구분이 필요하면
// libphonenumber-js의 parsePhoneNumberFromString을 써야 한다.
const PRIORITY_ISO2: CountryCode[] = [
  'KR',
  'US',
  'JP',
  'CN',
  'TW',
  'HK',
  'SG',
  'TH',
  'VN',
  'PH',
  'ID',
  'MY',
  'IN',
  'AU',
  'NZ',
  'CA',
  'GB',
  'DE',
  'FR',
  'IT',
  'ES',
];

// 일부 구형 런타임에서 Intl.DisplayNames 미지원 시 ISO2 로 폴백한다.
const regionNames = (() => {
  try {
    return new Intl.DisplayNames(['ko'], { type: 'region' });
  } catch {
    return undefined;
  }
})();

function toKoreanName(iso2: CountryCode): string {
  try {
    return regionNames?.of(iso2) ?? iso2;
  } catch {
    return iso2;
  }
}

// libphonenumber-js 가 아는 모든 국가를 생성한다.
function buildCountries(): Country[] {
  const countries: Country[] = [];
  for (const iso2 of getCountries()) {
    let dialCode: string;
    try {
      dialCode = getCountryCallingCode(iso2);
    } catch {
      continue; // 발신 코드가 없는 항목은 제외
    }
    countries.push({ iso2, dialCode, flag: toFlagEmoji(iso2), name: toKoreanName(iso2) });
  }

  const priorityRank = new Map(PRIORITY_ISO2.map((iso2, index) => [iso2, index]));
  return countries.sort((a, b) => {
    const ra = priorityRank.get(a.iso2);
    const rb = priorityRank.get(b.iso2);
    if (ra !== undefined && rb !== undefined) return ra - rb;
    if (ra !== undefined) return -1;
    if (rb !== undefined) return 1;
    return a.name.localeCompare(b.name, 'ko');
  });
}

export const COUNTRIES: readonly Country[] = buildCountries();

export const DEFAULT_COUNTRY: Country = COUNTRIES.find((c) => c.iso2 === 'KR') ?? COUNTRIES[0];

// dialCode 길이가 긴 순으로 정렬한 사본 (prefix 매칭 시 긴 코드 우선).
// 동일 dialCode(NANP의 '1')는 COUNTRIES 우선순위 앞쪽이 선택됨.
const COUNTRIES_BY_DIAL_CODE_DESC: readonly Country[] = [...COUNTRIES].sort(
  (a, b) => b.dialCode.length - a.dialCode.length,
);

/**
 * digits 문자열의 앞부분이 어떤 국가의 dialCode로 시작하는지 매칭. 가장 긴 dialCode 우선.
 *
 * 번호가 다 들어왔으면 libphonenumber에 먼저 묻는다 — 한 국가번호를 여러 나라가 나눠 쓰면
 * prefix 매칭은 가나다순으로 이긴 나라를 고른다(`+358` → 핀란드가 아니라 올란드 제도).
 * 입력 중인 부분 번호는 파싱이 안 되므로 그때만 prefix로 떨어진다.
 */
export function matchCountryByDigitPrefix(digits: string): Country | undefined {
  if (!digits) return undefined;
  const parsedIso2 = parsePhoneNumberFromString(`+${digits}`)?.country;
  const resolved = parsedIso2 && COUNTRIES.find((c) => c.iso2 === parsedIso2);
  if (resolved) return resolved;
  return COUNTRIES_BY_DIAL_CODE_DESC.find((c) => digits.startsWith(c.dialCode));
}

const placeholderCache = new Map<CountryCode, string>();

// 라이브러리와 examples.mobile.json 버전이 어긋나 예시가 없는 국가가 생길 때만 쓰인다.
const GENERIC_PLACEHOLDER = '전화번호 입력';

/**
 * 선택된 국가의 휴대폰 예시 번호를 그 나라 표기로 반환.
 * KR `010-2000-0000`, JP `090-1234-5678`, US `(201) 555-0123`.
 */
export function getPhonePlaceholder(iso2: CountryCode): string {
  const cached = placeholderCache.get(iso2);
  if (cached) return cached;

  const example = getExampleNumber(iso2, examples)?.formatNational();
  if (!example) return GENERIC_PLACEHOLDER;

  placeholderCache.set(iso2, example);
  return example;
}
